(function () {
  var DATALAYER_ENABLED = true;

  window.dataLayer = window.dataLayer || [];

  var bootNode = null;

  var isDataLayerEnabled = function () {
    return DATALAYER_ENABLED === true;
  };

  var getBoot = function () {
    if (!bootNode) {
      bootNode = document.querySelector('[data-datalayer-boot]');
    }
    return bootNode;
  };

  var clearEcommerce = function () {
    if (!isDataLayerEnabled()) {
      return;
    }
    window.dataLayer.push({ ecommerce: null });
  };

  var toNumber = function (value) {
    var parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 0;
    }
    return parsed;
  };

  var centsToUnit = function (cents) {
    return Math.round(toNumber(cents)) / 100;
  };

  var normalizeEmail = function (email) {
    return String(email || '')
      .trim()
      .toLowerCase();
  };

  var normalizePhone = function (phone) {
    var digits = String(phone || '').replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    if (digits.charAt(0) === '0') {
      digits = '90' + digits.slice(1);
    }
    if (digits.indexOf('90') !== 0) {
      digits = '90' + digits;
    }
    return '+' + digits;
  };

  var sha256Hex = function (text) {
    if (!text || !window.crypto || !window.crypto.subtle) {
      return Promise.resolve('');
    }
    var encoded = new TextEncoder().encode(text);
    return window.crypto.subtle.digest('SHA-256', encoded).then(function (buffer) {
      var bytes = Array.from(new Uint8Array(buffer));
      return bytes
        .map(function (byte) {
          return byte.toString(16).padStart(2, '0');
        })
        .join('');
    });
  };

  var buildUserData = function () {
    var boot = getBoot();
    if (!boot) {
      return Promise.resolve(null);
    }

    var emailRaw = boot.dataset.dlCustomerEmail || '';
    var phoneRaw = boot.dataset.dlCustomerPhone || '';
    var email = normalizeEmail(emailRaw);
    var phone = normalizePhone(phoneRaw);

    if (!email && !phone) {
      return Promise.resolve(null);
    }

    return Promise.all([email ? sha256Hex(email) : Promise.resolve(''), phone ? sha256Hex(phone) : Promise.resolve('')]).then(
      function (hashes) {
        var userData = {};
        if (hashes[0]) {
          userData.email = hashes[0];
        }
        if (hashes[1]) {
          userData.phone_number = hashes[1];
        }
        if (Object.keys(userData).length === 0) {
          return null;
        }
        return userData;
      }
    );
  };

  var getCustomerFields = function () {
    var boot = getBoot();
    var customerType = boot && boot.dataset.dlCustomerType ? boot.dataset.dlCustomerType : '';
    var customerId = '';
    if (boot && boot.dataset.dlCustomerId) {
      customerId = 'ID-' + String(boot.dataset.dlCustomerId);
    }
    return {
      customer_type: customerType,
      customer_id: customerId,
    };
  };

  var getAffiliation = function () {
    var boot = getBoot();
    if (boot && boot.dataset.dlAffiliation) {
      return boot.dataset.dlAffiliation;
    }
    return '';
  };

  var readListContext = function (card) {
    var listRoot = card.closest('[data-dl-item-list]');
    if (!listRoot) {
      return {
        item_list_id: '',
        item_list_name: '',
      };
    }
    return {
      item_list_id: listRoot.dataset.dlItemListId || '',
      item_list_name: listRoot.dataset.dlItemListName || '',
    };
  };

  var readItemFromCard = function (card) {
    var list = readListContext(card);
    var priceCents = card.dataset.dlPriceCents || '0';
    var compareCents = card.dataset.dlCompareAtCents || '0';
    var price = centsToUnit(priceCents);
    var compareAt = centsToUnit(compareCents);
    var discount = 0;
    if (compareAt > price) {
      discount = Math.round((compareAt - price) * 100) / 100;
    }

    return {
      item_id: card.dataset.dlItemId || '',
      item_name: card.dataset.dlItemName || '',
      affiliation: getAffiliation(),
      coupon: card.dataset.dlCoupon || '',
      discount: discount,
      index: toNumber(card.dataset.dlIndex),
      item_brand: card.dataset.dlItemBrand || '',
      item_category: card.dataset.dlItemCategory || '',
      item_category2: card.dataset.dlItemCategory2 || '',
      item_category3: card.dataset.dlItemCategory3 || '',
      item_category4: card.dataset.dlItemCategory4 || '',
      item_category5: card.dataset.dlItemCategory5 || '',
      item_list_id: list.item_list_id,
      item_list_name: list.item_list_name,
      item_variant: card.dataset.dlItemVariant || '',
      location_id: card.dataset.dlLocationId || '',
      promotion_id: card.dataset.dlPromotionId || '',
      promotion_name: card.dataset.dlPromotionName || '',
      price: price,
      quantity: 1,
    };
  };

  var readItemsFromList = function (listRoot) {
    if (!listRoot) {
      return [];
    }

    var cards = listRoot.querySelectorAll('[data-datalayer-product]');
    var items = [];

    cards.forEach(function (card) {
      var item = readItemFromCard(card);
      if (item.item_id || item.item_name) {
        items.push(item);
      }
    });

    return items;
  };

  var pushViewItemList = function (listRoot) {
    if (!isDataLayerEnabled()) {
      return;
    }

    if (!listRoot) {
      return;
    }

    var items = readItemsFromList(listRoot);
    if (items.length === 0) {
      return;
    }

    var listId = listRoot.dataset.dlItemListId || '';
    var listName = listRoot.dataset.dlItemListName || '';
    var customerFields = getCustomerFields();

    buildUserData().then(function (userData) {
      clearEcommerce();
      var payload = {
        event: 'view_item_list',
        customer_type: customerFields.customer_type,
        customer_id: customerFields.customer_id,
        ecommerce: {
          item_list_id: listId,
          item_list_name: listName,
          items: items,
        },
      };
      if (userData) {
        payload.user_data = userData;
      }
      window.dataLayer.push(payload);
    });
  };

  var buildItemFromCartLine = function (line, form) {
    if (!line) {
      return null;
    }

    var quantity = toNumber(line.quantity);
    if (quantity < 1) {
      quantity = 1;
    }

    var priceCents = toNumber(line.price);
    var price = centsToUnit(priceCents);
    var itemId = line.sku || line.variant_id || '';
    var itemName = line.product_title || line.title || '';
    var variantTitle = line.variant_title || '';

    if (variantTitle === 'Default Title') {
      variantTitle = '';
    }

    var card = form && form.closest ? form.closest('[data-datalayer-product]') : null;
    var list = card ? readListContext(card) : { item_list_id: '', item_list_name: '' };
    var category = '';
    var category2 = '';

    if (form && form.dataset) {
      category = form.dataset.dlItemCategory || '';
      category2 = form.dataset.dlItemCategory2 || '';
    }

    if (card) {
      var cardItem = readItemFromCard(card);
      return {
        item_id: String(itemId || cardItem.item_id),
        item_name: itemName || cardItem.item_name,
        affiliation: getAffiliation(),
        coupon: cardItem.coupon || '',
        discount: cardItem.discount || 0,
        index: cardItem.index,
        item_brand: line.vendor || cardItem.item_brand || '',
        item_category: category || cardItem.item_category || '',
        item_category2: category2 || cardItem.item_category2 || '',
        item_category3: cardItem.item_category3 || '',
        item_category4: cardItem.item_category4 || '',
        item_category5: cardItem.item_category5 || '',
        item_list_id: list.item_list_id,
        item_list_name: list.item_list_name,
        item_variant: variantTitle || cardItem.item_variant || '',
        location_id: cardItem.location_id || '',
        promotion_id: cardItem.promotion_id || '',
        promotion_name: cardItem.promotion_name || '',
        price: price,
        quantity: quantity,
      };
    }

    return {
      item_id: String(itemId),
      item_name: itemName,
      affiliation: getAffiliation(),
      coupon: '',
      discount: 0,
      index: 0,
      item_brand: line.vendor || '',
      item_category: category,
      item_category2: category2,
      item_category3: '',
      item_category4: '',
      item_category5: '',
      item_list_id: list.item_list_id,
      item_list_name: list.item_list_name,
      item_variant: variantTitle,
      location_id: '',
      promotion_id: '',
      promotion_name: '',
      price: price,
      quantity: quantity,
    };
  };

  var pushAddToCart = function (line, form, currency) {
    if (!isDataLayerEnabled()) {
      return;
    }

    var item = buildItemFromCartLine(line, form);
    if (!item || (!item.item_id && !item.item_name)) {
      return;
    }

    var customerFields = getCustomerFields();
    var value = Math.round(item.price * item.quantity * 100) / 100;
    var resolvedCurrency = currency || '';

    if (!resolvedCurrency) {
      var boot = getBoot();
      if (boot && boot.dataset.dlCurrency) {
        resolvedCurrency = boot.dataset.dlCurrency;
      }
    }

    buildUserData().then(function (userData) {
      clearEcommerce();
      var payload = {
        event: 'add_to_cart',
        customer_type: customerFields.customer_type,
        customer_id: customerFields.customer_id,
        ecommerce: {
          currency: resolvedCurrency,
          value: value,
          items: [item],
        },
      };
      if (userData) {
        payload.user_data = userData;
      }
      window.dataLayer.push(payload);
    });
  };

  var pushRemoveFromCart = function (lineItem, quantityRemoved, currency, lineIndex) {
    if (!isDataLayerEnabled()) {
      return;
    }

    if (!lineItem || !quantityRemoved || quantityRemoved < 1) {
      return;
    }

    var index = toNumber(lineIndex);
    if (index < 1) {
      index = 1;
    }

    var item = buildItemFromCartJsItem(lineItem, index);
    if (!item || (!item.item_id && !item.item_name)) {
      return;
    }

    item.quantity = quantityRemoved;

    var customerFields = getCustomerFields();
    var value = Math.round(item.price * quantityRemoved * 100) / 100;
    var resolvedCurrency = currency || getCurrency();

    buildUserData().then(function (userData) {
      clearEcommerce();
      var payload = {
        event: 'remove_from_cart',
        customer_type: customerFields.customer_type,
        customer_id: customerFields.customer_id,
        ecommerce: {
          currency: resolvedCurrency,
          value: value,
          items: [item],
        },
      };
      if (userData) {
        payload.user_data = userData;
      }
      window.dataLayer.push(payload);
    });
  };

  var initAddToCart = function () {
    document.addEventListener('joker:cart-add-success', function (event) {
      if (!event || !event.detail || !event.detail.line) {
        return;
      }
      pushAddToCart(event.detail.line, event.detail.form, event.detail.currency);
    });
  };

  var initRemoveFromCart = function () {
    document.addEventListener('joker:cart-remove-success', function (event) {
      if (!event || !event.detail || !event.detail.lineItem) {
        return;
      }
      pushRemoveFromCart(
        event.detail.lineItem,
        event.detail.quantity,
        event.detail.currency,
        event.detail.lineIndex
      );
    });
  };

  var getCurrency = function () {
    var boot = getBoot();
    if (boot && boot.dataset.dlCurrency) {
      return boot.dataset.dlCurrency;
    }
    return '';
  };

  var buildItemFromCartJsItem = function (item, index) {
    if (!item) {
      return null;
    }

    var quantity = toNumber(item.quantity);
    if (quantity < 1) {
      quantity = 1;
    }

    var priceCents = toNumber(item.final_price !== undefined ? item.final_price : item.price);
    var originalCents = toNumber(item.original_price || priceCents);
    var price = centsToUnit(priceCents);
    var discount = 0;

    if (originalCents > priceCents) {
      discount = Math.round(centsToUnit(originalCents - priceCents) * 100) / 100;
    }

    var variantTitle = item.variant_title || '';
    if (variantTitle === 'Default Title') {
      variantTitle = '';
    }

    return {
      item_id: String(item.sku || item.variant_id || ''),
      item_name: item.product_title || item.title || '',
      affiliation: getAffiliation(),
      coupon: '',
      discount: discount,
      index: index,
      item_brand: item.vendor || '',
      item_category: item.product_type || '',
      item_category2: '',
      item_category3: '',
      item_category4: '',
      item_category5: '',
      item_list_id: '',
      item_list_name: '',
      item_variant: variantTitle,
      location_id: '',
      promotion_id: '',
      promotion_name: '',
      price: price,
      quantity: quantity,
    };
  };

  var buildItemsFromCart = function (cart) {
    if (!cart || !cart.items || !cart.items.length) {
      return [];
    }

    var items = [];

    cart.items.forEach(function (line, lineIndex) {
      var item = buildItemFromCartJsItem(line, lineIndex + 1);
      if (item && (item.item_id || item.item_name)) {
        items.push(item);
      }
    });

    return items;
  };

  var pushViewCart = function (cart) {
    if (!isDataLayerEnabled()) {
      return;
    }

    if (!cart) {
      return;
    }

    var items = buildItemsFromCart(cart);
    var currency = cart.currency || getCurrency();
    var value = centsToUnit(cart.total_price || 0);
    var customerFields = getCustomerFields();

    buildUserData().then(function (userData) {
      clearEcommerce();
      var payload = {
        event: 'view_cart',
        customer_type: customerFields.customer_type,
        customer_id: customerFields.customer_id,
        ecommerce: {
          currency: currency,
          value: value,
          items: items,
        },
      };
      if (userData) {
        payload.user_data = userData;
      }
      window.dataLayer.push(payload);
    });
  };

  var initViewCart = function () {
    document.addEventListener('joker:cart-viewed', function (event) {
      if (!event || !event.detail || !event.detail.cart) {
        return;
      }
      pushViewCart(event.detail.cart);
    });

    var cartPage = document.querySelector('[data-datalayer-cart-page]');
    if (!cartPage) {
      return;
    }

    fetch('/cart.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Cart load failed');
        }
        return response.json();
      })
      .then(function (cart) {
        pushViewCart(cart);
      })
      .catch(function () {});
  };

  var getCouponFromCart = function (cart) {
    if (!cart) {
      return '';
    }

    if (cart.discount_codes && cart.discount_codes.length) {
      var codes = [];

      cart.discount_codes.forEach(function (entry) {
        if (entry && entry.applicable && entry.code) {
          codes.push(entry.code);
        }
      });

      if (codes.length) {
        return codes.join(',');
      }
    }

    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length) {
      var titles = [];

      cart.cart_level_discount_applications.forEach(function (entry) {
        if (entry && entry.title) {
          titles.push(entry.title);
        }
      });

      if (titles.length) {
        return titles.join(',');
      }
    }

    return '';
  };

  var pushBeginCheckout = function (cart) {
    if (!isDataLayerEnabled()) {
      return Promise.resolve();
    }

    if (!cart) {
      return Promise.resolve();
    }

    var items = buildItemsFromCart(cart);
    var currency = cart.currency || getCurrency();
    var value = centsToUnit(cart.total_price || 0);
    var coupon = getCouponFromCart(cart);
    var customerFields = getCustomerFields();

    return buildUserData().then(function (userData) {
      clearEcommerce();
      var ecommerce = {
        currency: currency,
        value: value,
        items: items,
      };

      if (coupon) {
        ecommerce.coupon = coupon;
      }

      var payload = {
        event: 'begin_checkout',
        customer_type: customerFields.customer_type,
        customer_id: customerFields.customer_id,
        ecommerce: ecommerce,
      };

      if (userData) {
        payload.user_data = userData;
      }

      window.dataLayer.push(payload);
    });
  };

  var pushSearch = function (searchTerm) {
    if (!isDataLayerEnabled()) {
      return;
    }

    var term = String(searchTerm || '').trim();
    if (!term) {
      return;
    }

    var customerFields = getCustomerFields();

    try {
      sessionStorage.setItem('joker-dl-search-last', term);
    } catch (storageError) {}

    buildUserData().then(function (userData) {
      var payload = {
        event: 'search',
        customer_type: customerFields.customer_type,
        customer_id: customerFields.customer_id,
        search_term: term,
      };

      if (userData) {
        payload.user_data = userData;
      }

      window.dataLayer.push(payload);
    });
  };

  var initSearch = function () {
    document.addEventListener(
      'submit',
      function (event) {
        if (!isDataLayerEnabled()) {
          return;
        }

        var form = event.target;
        if (!form || form.tagName !== 'FORM') {
          return;
        }

        if (form.getAttribute('role') !== 'search') {
          return;
        }

        var input = form.querySelector('input[name="q"]');
        if (!input) {
          return;
        }

        var term = String(input.value || '').trim();
        if (!term) {
          return;
        }

        pushSearch(term);
      },
      true
    );

    var termNode = document.querySelector('[data-dl-search-term]');
    if (!termNode) {
      return;
    }

    var pageTerm = '';
    try {
      pageTerm = JSON.parse(termNode.textContent);
    } catch (parseError) {
      pageTerm = '';
    }

    pageTerm = String(pageTerm || '').trim();
    if (!pageTerm) {
      return;
    }

    var lastTerm = '';
    try {
      lastTerm = sessionStorage.getItem('joker-dl-search-last') || '';
    } catch (storageError) {
      lastTerm = '';
    }

    if (lastTerm === pageTerm) {
      return;
    }

    pushSearch(pageTerm);
  };

  var initBeginCheckout = function () {
    document.addEventListener('click', function (event) {
      if (!isDataLayerEnabled()) {
        return;
      }

      if (event.defaultPrevented) {
        return;
      }

      var target = event.target;
      if (!target || !target.closest) {
        return;
      }

      var link = target.closest('[data-datalayer-cart-page] .cart-page__checkout');
      if (!link) {
        return;
      }

      var href = link.getAttribute('href') || '/checkout';
      event.preventDefault();

      fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Cart load failed');
          }
          return response.json();
        })
        .then(function (cart) {
          return pushBeginCheckout(cart).then(function () {
            return href;
          });
        })
        .then(function (destination) {
          window.location.href = destination;
        })
        .catch(function () {
          window.location.href = href;
        });
    });
  };

  var initViewItemList = function () {
    document.querySelectorAll('[data-dl-item-list]').forEach(function (listRoot) {
      pushViewItemList(listRoot);
    });

    document.addEventListener('joker:datalayer-list-updated', function (event) {
      if (!event || !event.detail || !event.detail.listRoot) {
        return;
      }
      pushViewItemList(event.detail.listRoot);
    });
  };

  var parseViewItemContext = function () {
    var node = document.querySelector('[data-datalayer-view-item]');
    if (!node) {
      return null;
    }
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  var findVariantRecord = function (context, variantId) {
    if (!context || !Array.isArray(context.variants)) {
      return null;
    }
    var targetId = toNumber(variantId);
    var match = null;
    context.variants.forEach(function (entry) {
      if (match) {
        return;
      }
      if (toNumber(entry.id) === targetId) {
        match = entry;
      }
    });
    return match;
  };

  var buildItemFromVariantRecord = function (context, variantRecord) {
    if (!context || !variantRecord) {
      return null;
    }

    var price = centsToUnit(variantRecord.priceCents);
    var compareAt = centsToUnit(variantRecord.compareAtCents);
    var discount = 0;
    if (compareAt > price) {
      discount = Math.round((compareAt - price) * 100) / 100;
    }

    return {
      item_id: variantRecord.itemId || '',
      item_name: context.productTitle || '',
      affiliation: getAffiliation(),
      coupon: '',
      discount: discount,
      index: toNumber(variantRecord.index),
      item_brand: context.itemBrand || '',
      item_category: context.itemCategory || '',
      item_category2: context.itemCategory2 || '',
      item_category3: '',
      item_category4: '',
      item_category5: '',
      item_list_id: '',
      item_list_name: '',
      item_variant: variantRecord.itemVariant || '',
      location_id: '',
      price: price,
      quantity: 1,
    };
  };

  var resolveItemFromFavoriteButton = function (button) {
    if (!button) {
      return null;
    }

    var card = button.closest('[data-datalayer-product]');
    if (card) {
      var cardItem = readItemFromCard(card);
      cardItem.quantity = 1;
      return cardItem;
    }

    var context = parseViewItemContext();
    if (context) {
      var variantInput = document.querySelector('[data-product-variant-id]');
      var variantId = context.initialVariantId;
      if (variantInput && variantInput.value) {
        variantId = variantInput.value;
      }
      var variantRecord = findVariantRecord(context, variantId);
      return buildItemFromVariantRecord(context, variantRecord);
    }

    var title = button.dataset.productTitle || '';
    var productId = button.dataset.productId || '';
    var numericMatch = String(productId).match(/(\d+)\s*$/);
    var idStr = numericMatch ? numericMatch[1] : productId;

    if (!title && !idStr) {
      return null;
    }

    return {
      item_id: String(idStr),
      item_name: title,
      affiliation: getAffiliation(),
      coupon: '',
      discount: 0,
      index: 0,
      item_brand: '',
      item_category: '',
      item_category2: '',
      item_category3: '',
      item_category4: '',
      item_category5: '',
      item_list_id: '',
      item_list_name: '',
      item_variant: '',
      location_id: '',
      promotion_id: '',
      promotion_name: '',
      price: 0,
      quantity: 1,
    };
  };

  var pushAddToWishlist = function (button) {
    if (!isDataLayerEnabled()) {
      return;
    }

    var item = resolveItemFromFavoriteButton(button);
    if (!item || (!item.item_id && !item.item_name)) {
      return;
    }

    var customerFields = getCustomerFields();
    var value = Math.round(item.price * item.quantity * 100) / 100;
    var resolvedCurrency = getCurrency();
    var context = parseViewItemContext();
    if (context && context.currency) {
      resolvedCurrency = context.currency;
    }

    buildUserData().then(function (userData) {
      clearEcommerce();
      var payload = {
        event: 'add_to_wishlist',
        customer_type: customerFields.customer_type,
        customer_id: customerFields.customer_id,
        ecommerce: {
          currency: resolvedCurrency,
          value: value,
          items: [item],
        },
      };
      if (userData) {
        payload.user_data = userData;
      }
      window.dataLayer.push(payload);
    });
  };

  var initAddToWishlist = function () {
    document.addEventListener('joker:favorite-added', function (event) {
      if (!event || !event.detail || !event.detail.button) {
        return;
      }
      pushAddToWishlist(event.detail.button);
    });
  };

  var pushViewItem = function (variantId) {
    if (!isDataLayerEnabled()) {
      return;
    }

    var context = parseViewItemContext();
    if (!context) {
      return;
    }

    var resolvedVariantId = variantId || context.initialVariantId;
    var variantRecord = findVariantRecord(context, resolvedVariantId);
    if (!variantRecord) {
      return;
    }

    var item = buildItemFromVariantRecord(context, variantRecord);
    if (!item || (!item.item_id && !item.item_name)) {
      return;
    }

    var customerFields = getCustomerFields();
    var value = Math.round(item.price * item.quantity * 100) / 100;
    var currency = context.currency || '';

    buildUserData().then(function (userData) {
      clearEcommerce();
      var payload = {
        event: 'view_item',
        customer_type: customerFields.customer_type,
        customer_id: customerFields.customer_id,
        ecommerce: {
          currency: currency,
          value: value,
          items: [item],
        },
      };
      if (userData) {
        payload.user_data = userData;
      }
      window.dataLayer.push(payload);
    });
  };

  var initViewItem = function () {
    if (!parseViewItemContext()) {
      return;
    }

    var context = parseViewItemContext();
    pushViewItem(context.initialVariantId);

    document.addEventListener('joker:product-variant-selected', function (event) {
      if (!event || !event.detail) {
        return;
      }
      pushViewItem(event.detail.variantId);
    });
  };

  var pushSelectItem = function (card) {
    if (!isDataLayerEnabled()) {
      return;
    }

    if (!card) {
      return;
    }

    var item = readItemFromCard(card);
    if (!item.item_id && !item.item_name) {
      return;
    }

    var customerFields = getCustomerFields();

    buildUserData().then(function (userData) {
      clearEcommerce();
      var payload = {
        event: 'select_item',
        customer_type: customerFields.customer_type,
        customer_id: customerFields.customer_id,
        ecommerce: {
          item_list_id: item.item_list_id,
          item_list_name: item.item_list_name,
          items: [item],
        },
      };
      if (userData) {
        payload.user_data = userData;
      }
      window.dataLayer.push(payload);
    });
  };

  var initSelectItem = function () {
    document.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) {
          return;
        }
        var link = target.closest('a.product-card__title-link, a.product-card__media');
        if (!link) {
          return;
        }
        var card = link.closest('[data-datalayer-product]');
        if (!card) {
          return;
        }
        var listRoot = card.closest('[data-dl-item-list]');
        if (!listRoot) {
          return;
        }
        pushSelectItem(card);
      },
      true
    );
  };

  var init = function () {
    if (!isDataLayerEnabled()) {
      return;
    }
    initSelectItem();
    initViewItem();
    initViewItemList();
    initAddToCart();
    initRemoveFromCart();
    initViewCart();
    initBeginCheckout();
    initSearch();
    initAddToWishlist();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.jokerDataLayer = window.jokerDataLayer || {};
  window.jokerDataLayer.isEnabled = isDataLayerEnabled;
  window.jokerDataLayer.clearEcommerce = clearEcommerce;
  window.jokerDataLayer.pushSelectItem = pushSelectItem;
  window.jokerDataLayer.pushViewItem = pushViewItem;
  window.jokerDataLayer.pushViewItemList = pushViewItemList;
  window.jokerDataLayer.pushAddToCart = pushAddToCart;
  window.jokerDataLayer.pushRemoveFromCart = pushRemoveFromCart;
  window.jokerDataLayer.pushViewCart = pushViewCart;
  window.jokerDataLayer.pushBeginCheckout = pushBeginCheckout;
  window.jokerDataLayer.pushSearch = pushSearch;
  window.jokerDataLayer.pushAddToWishlist = pushAddToWishlist;
})();
